import React from "react";
import { m } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Trash2, ShoppingBag, Heart, Check } from "lucide-react";
import CategoryManager from "../categories/CategoryManager";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { Capacitor } from "@capacitor/core";
import { openExternalUrl } from "@/utils";
import { apiClient } from "@/api/apiClient";
import { useLikedProducts } from "@/hooks/useLikedProducts";

const MatchCard = React.memo(function MatchCard({
    product,
    onDelete,
    isSelectable = false,
    isSelected = false,
    onToggleSelect,
    ...props
}) {
    const { isLiked, toggleLike } = useLikedProducts();
    const liked = isLiked(product?.id);

    const originalPriceNum = parseFloat(product?.original_price);
    const priceNum = parseFloat(product?.price);
    const hasDiscount = !isNaN(originalPriceNum) && !isNaN(priceNum) && originalPriceNum > priceNum;

    // Check if sizes is effectively empty or contains only empty strings/nulls OR all sizes are marked "Out of Stock"
    const isOutOfStock = product?.stock_status === 'rupture' ||
        !product?.sizes ||
        product.sizes.length === 0 ||
        (product.sizes.length === 1 && !product.sizes[0]) ||
        product.sizes.every(size => typeof size === 'string' && size.includes('(Out of Stock'));

    const navigate = useNavigate();

    const handleCardClick = (e) => {
        // If in selection mode, toggle selection on card click
        if (isSelectable && onToggleSelect) {
            onToggleSelect(product, e);
            return;
        }

        // Prevent navigation if clicking on buttons or their children
        if (
            e.target.closest('button') ||
            e.target.closest('a') ||
            e.target.closest('[role="button"]')
        ) {
            return;
        }
        navigate(`/product?id=${product.id}`, {
            state: { from: props.source || 'default' }
        });
    };

    const handleQuickLike = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleLike(product, e);
        if (props.onLikeChange) {
            props.onLikeChange(product, !liked);
        }
        if (liked && onDelete) {
            onDelete(product.id || product);
        }
    };

    return (
        <m.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={handleCardClick}
            className="cursor-pointer"
        >
            <Card className={`overflow-hidden transition-all duration-300 group dark:bg-[hsl(225,14%,10%)] dark:border-gray-800 ${
                isSelected
                    ? "ring-2 ring-amber-500 border-amber-500 shadow-lg shadow-amber-500/10 scale-[1.01]"
                    : "hover:shadow-xl"
            }`}>
                <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    <OptimizedImage
                        src={product.images[0]}
                        alt={product.name}
                        className={`w-full h-full object-cover transition-transform duration-500 ${
                            isSelectable ? '' : 'group-hover:scale-110'
                        }`}
                        width={400} // Appropriate width for grid card
                    />

                    {/* Checkbox de sélection multiple (en haut à gauche) */}
                    {isSelectable && (
                        <m.button
                            type="button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onToggleSelect) onToggleSelect(product, e);
                            }}
                            className={`absolute top-2.5 left-2.5 z-20 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 shadow-md ${
                                isSelected
                                    ? "bg-amber-500 text-white shadow-amber-500/30 ring-2 ring-white dark:ring-zinc-900 scale-105"
                                    : "bg-black/45 dark:bg-black/60 text-transparent hover:text-white/60 border-2 border-white/70 hover:border-white backdrop-blur-md"
                            }`}
                            title={isSelected ? "Désélectionner" : "Sélectionner"}
                            aria-label={isSelected ? "Désélectionner" : "Sélectionner"}
                        >
                            <Check size={14} className={isSelected ? "stroke-[3] text-white" : "opacity-0"} />
                        </m.button>
                    )}

                    {/* Badges statut & nouveauté */}
                    <div className={`absolute left-2.5 flex flex-col gap-1.5 z-10 pointer-events-none ${
                        isSelectable ? 'top-11' : 'top-2.5'
                    }`}>
                        {props.isNew && (
                            <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white border-none font-bold shadow-md px-2.5 py-0.5 text-[11px] uppercase tracking-wider">
                                NOUVEAU
                            </Badge>
                        )}
                        {hasDiscount && (
                            <Badge className="bg-red-500 text-white font-bold shadow-md px-2 py-0.5 text-[11px]">
                                -{Math.round((1 - priceNum / originalPriceNum) * 100)}%
                            </Badge>
                        )}
                        {isOutOfStock && (
                            <Badge className="bg-gray-900/90 text-white border border-white/20 shadow-md px-2 py-0.5 text-[10px]">
                                Rupture
                            </Badge>
                        )}
                    </div>

                    {/* Bouton Like / Coup de cœur direct en haut à droite */}
                    <m.button
                        type="button"
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.85 }}
                        onClick={handleQuickLike}
                        className={`absolute top-2.5 right-2.5 z-20 p-2 rounded-full backdrop-blur-md transition-all duration-200 shadow-md flex items-center justify-center ${
                            liked
                                ? "bg-white/95 dark:bg-zinc-900/95 text-red-500 shadow-red-500/20 ring-2 ring-red-500/30"
                                : "bg-black/35 dark:bg-black/55 text-white hover:bg-white/95 dark:hover:bg-zinc-900/95 hover:text-red-500 dark:hover:text-red-400 border border-white/25 hover:border-white/40"
                        }`}
                        title={liked ? "Retirer des coups de cœur" : "Ajouter aux coups de cœur"}
                        aria-label={liked ? "Retirer des coups de cœur" : "Ajouter aux coups de cœur"}
                    >
                        <Heart
                            size={16}
                            className={`transition-all duration-200 ${liked ? "fill-red-500 text-red-500" : "fill-transparent"}`}
                        />
                    </m.button>

                    {!isSelectable && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="absolute bottom-3 left-3 right-3 flex">
                                {product.product_url && (
                                    <a
                                        href={product.product_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 w-full"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            apiClient.entities.UserInteraction.create({
                                                product_id: product.id,
                                                action: 'visit_marque_site'
                                            });
                                            if (Capacitor.isNativePlatform()) {
                                                openExternalUrl(product.product_url, e);
                                            }
                                        }}
                                    >
                                        <Button className="w-full bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-white" size="sm">
                                            <ShoppingBag className="w-4 h-4 mr-1" />
                                            Acheter
                                        </Button>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-lg text-gray-900 dark:text-white line-clamp-1 break-words">{product.name}</h3>
                            {product.brand_slug ? (
                                <Link
                                    to={`/brand/${product.brand_slug}`}
                                    className="text-sm text-amber-600 dark:text-amber-400 font-medium hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {product.brand}
                                </Link>
                            ) : (
                                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">{product.brand}</p>
                            )}
                        </div>
                        <div className="text-right ml-2 shrink-0">
                            <div className="font-bold text-lg text-gray-900 dark:text-white">{product.price}€</div>
                            {hasDiscount && (
                                <div className="text-xs text-gray-400 line-through">
                                    {product.original_price}€
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Catégories personnalisées */}
                    <div className="mb-3">
                        <CategoryManager
                            productId={product.id}
                            size="sm"
                        />
                    </div>

                    {product.sizes && product.sizes.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {product.sizes.slice(0, 4).map((size, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs dark:border-gray-700 dark:text-gray-300">
                                    {size}
                                </Badge>
                            ))}
                            {product.sizes.length > 4 && (
                                <Badge variant="outline" className="text-xs dark:border-gray-700 dark:text-gray-300">
                                    +{product.sizes.length - 4}
                                </Badge>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </m.div>
    );
});

export default MatchCard;